import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import { API_URL, IMAGE_URL } from "../../global";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const RecentList = ({ handleRecentpatientClick }) => {
  const [chartData, setChartData] = useState([]);
  const [groupedPatients, setGroupedPatients] = useState({});
  const [recentPatients, setRecentPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const containerStyle = {
    display: "flex",
    gap: "20px",
    padding: "20px",
  };

  const cardStyle = {
    flex: 1,
    padding: "20px",
    borderRadius: "10px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#ffffff",
  };

  const patientItemStyle = {
    display: "flex",
    alignItems: "center",
    padding: "10px 0",
    borderBottom: "1px solid #eee",
  };

  const avatarStyle = {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    marginRight: "10px",
  };

  const contactStyle = {
    marginLeft: "auto",
    cursor: "pointer",
  };

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const userId = sessionStorage.getItem("user_id");
        const response = await fetch(`${API_URL}/case_info?user_id=${encodeURIComponent(userId)}`);
        if (!response.ok) throw new Error("Failed to fetch patient data");

        const data = await response.json();

        // Group patients by fk_pc_id
        const grouped = {};
        console.log(data);
        data.forEach((patient) => {
          const key = patient.fk_pc_id;

          if (!grouped[key]) {
            grouped[key] = {
              patient_name: patient.patient_name,
              pk_pc_id: patient.pk_pc_id,

              images: [],
            };
          }

          grouped[key].images.push({
            case_id: patient.pk_case_id,
            file_name: patient.file_name,
            gender: patient.gender,
            patient_dob: patient.patient_dob,
            patient_id: patient.patient_id,
            title: patient.title || "No Title",
            image: `${IMAGE_URL}/${patient.file_name}`,
            summary: patient.summary || "No summary available.",
            accession_no: patient.accession_no,
            organization: patient.organization,
            provider: patient.provider,
            service_date: patient.service_date,
            data_points:
              patient.data_points || "<p>No observations available.</p>",
            cm_created_at: patient.cm_created_at, // Ensure case timestamp is included
          });
        });

        console.log("Grouped Patients:", grouped);

        // Flatten the grouped data and sort by `cm_created_at` in descending order
        // let patientsList = Object.values(grouped)
        //   .map((p) => ({
        //     name: p.patient_name,
        //     patient_name: p.patient_name,
        //     patient_id: p.patient_id,
        //     gender: p.gender,
        //     accession_no: p.accession_no,
        //     patient_dob: p.patient_dob,
        //     cm_created_at: p.cm_created_at,
        //     file_name: p.images.file_name,
        //     provider: p.provider,
        //     organization: p.organization,
        //     service_date: p.service_date,
        //     data_points: p.data_points,
        //     // file_name:p.images.file_name,
        //     // image: `${IMAGE_URL}/${image.file_name}`,
        //     summary: p.summary || "No summary available.",
        //   }))
        //   .filter((p) => p.cm_created_at) // Remove entries without timestamps
        //   .sort((a, b) => new Date(b.cm_created_at) - new Date(a.cm_created_at)) // Sort by date (newest first)
        //   .slice(0, 5); // Take the last 5 patients

        let patientsList = Object.values(grouped)
          .map((p) => {
            // Sort patient's images to get the most recent one
            const latestImage = p.images
              .filter((img) => img.cm_created_at)
              .sort(
                (a, b) => new Date(b.cm_created_at) - new Date(a.cm_created_at)
              )[0];

            return {
              name: p.patient_name,
              patient_name: p.patient_name,
              patient_id: latestImage?.patient_id,
              gender: latestImage?.gender,
              accession_no: latestImage?.accession_no,
              patient_dob: latestImage?.patient_dob,
              cm_created_at: latestImage?.cm_created_at,
              file_name: latestImage?.file_name,
              title: latestImage?.title,
              image: latestImage?.image,
              provider: latestImage?.provider,
              organization: latestImage?.organization,
              service_date: latestImage?.service_date,
              data_points: latestImage?.data_points,
              summary: latestImage?.summary || "No summary available.",
            };
          })
          .filter((p) => p.cm_created_at) // Remove entries without timestamps
          .sort((a, b) => new Date(b.cm_created_at) - new Date(a.cm_created_at)) // Sort by date (newest first)
          .slice(0, 5);
        setRecentPatients(patientsList);
        setGroupedPatients(grouped);

        console.log(patientsList);
      } catch (error) {
        console.error("Error fetching patients:", error);
      }
    };

    fetchPatients();
  }, []);

  useEffect(() => {
    const fetchCSVData = async () => {
      try {
        const response = await fetch("/data.csv"); // Ensure the CSV is in the public folder
        const reader = await response.text();

        // Parse CSV data
        Papa.parse(reader, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            console.log("Raw CSV Data:", result.data); // Debugging
            const rawData = result.data;

            // Define fixed years 2019-2025
            const fixedYears = [
              "2019",
              "2020",
              "2021",
              "2022",
              "2023",
              "2024",
              "2025",
            ];

            // Initialize yearly data
            const yearlyData = fixedYears.reduce((acc, year) => {
              acc[year] = { name: year, Male: 0, Female: 0, Total: 0 };
              return acc;
            }, {});

            // Function to extract year correctly
            const extractYear = (dateString) => {
              if (!dateString) return null;

              // If the date is in "YYYY-MM-DD" format
              if (dateString.includes("-")) {
                return dateString.split("-")[0]; // Extract year
              }

              // If the date is in "DD/MM/YYYY" or "M/D/YYYY" format
              const parts = dateString.split("/");
              if (parts.length === 3) {
                return parts[2]; // Extract year
              }

              return null; // If format is invalid
            };

            // Process CSV data
            rawData.forEach((row, index) => {
              const year = extractYear(row.date?.trim());

              if (!year || !yearlyData[year]) {
                console.warn(`Invalid date format at row ${index}:`, row.date);
                return; // Skip invalid rows
              }

              // Add values to the correct year
              yearlyData[year].Male += parseInt(row.arrivals_male, 10) || 0;
              yearlyData[year].Female += parseInt(row.arrivals_female, 10) || 0;
              yearlyData[year].Total += parseInt(row.arrivals, 10) || 0;
            });
            setChartData(Object.values(yearlyData));
          },
        });
      } catch (error) {
        console.error("Error loading CSV:", error);
      }
    };

    fetchCSVData();
  }, []);

  const capitalize = (str) => {
    if (!str) return ""; // Handle null/undefined cases
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <div style={containerStyle}>
      {/* Recently Recovered Patients */}
      <div style={cardStyle}>
        <h3>Recent Patients</h3>
        {/* {recentPatients?.length > 0 ? (
          recentPatients.map((patient, index) => (
            <div key={index} style={patientItemStyle}>
              <div>{patient.name}</div>
            </div>
          ))
        ) : (
          <p>No recent patients found.</p>
        )} */}
        {recentPatients?.length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f9fafb", textAlign: "left" }}>
                <th
                  style={{ padding: "8px", borderBottom: "1px solid #e5e7eb" }}
                >
                  S.No
                </th>
                <th
                  style={{ padding: "8px", borderBottom: "1px solid #e5e7eb" }}
                >
                  Name
                </th>
                <th
                  style={{ padding: "8px", borderBottom: "1px solid #e5e7eb" }}
                >
                  Accession No
                </th>
                <th
                  style={{ padding: "8px", borderBottom: "1px solid #e5e7eb" }}
                >
                  DOB
                </th>
                <th
                  style={{ padding: "8px", borderBottom: "1px solid #e5e7eb" }}
                >
                  Created
                </th>
                <th
                  style={{ padding: "8px", borderBottom: "1px solid #e5e7eb" }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {recentPatients.map((patient, index) => (
                <tr key={index}>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {index + 1}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {capitalize(patient.patient_name)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {patient.accession_no}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {patient.patient_dob}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {new Date(patient.cm_created_at).toLocaleDateString()}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <button
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#3b82f6",
                        fontSize: "16px",
                      }}
                      onClick={() => handleRecentpatientClick(patient)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ padding: "10px", color: "#6b7280" }}>
            No recent patients found.
          </p>
        )}
      </div>

      {/* COVID Cases Yearly */}
      <div style={cardStyle}>
        <h3>Arrivals - Year Wise</h3>
        <p style={{ color: "#888", marginBottom: "10px" }}>Gender based data</p>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid stroke="#f5f5f5" />
            <XAxis dataKey="name" />
            <YAxis
              width={80}
              tickFormatter={(value) => value.toLocaleString()}
              allowDecimals={false}
            />
            <Tooltip />
            <Legend />
            <Line
              type="linear"
              dataKey="Male"
              stroke="#93c5fd"
              strokeWidth={3}
              dot={{ r: 3 }}
            />
            <Line
              type="linear"
              dataKey="Female"
              stroke="#fca5a5"
              strokeWidth={3}
              dot={{ r: 3 }}
            />
            <Line
              type="linear"
              dataKey="Total"
              stroke="#d8b4fe"
              strokeWidth={3}
              dot={{ r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RecentList;
